/**
 * ✅ RESTAURANT MASA İŞLEMLERİ HOOK'U
 * 
 * Bu hook, restoran modunda masa işlemlerini yönetir:
 * - handleConfirmOpenTable: Adisyon açma onayı
 * - handleCancelOpenTable: Adisyon açma iptali
 * - handleOpenTransferTableModal: Masa taşıma modalını aç
 * - handleTransferTable: Masa taşıma işlemi
 * - handleRequestMerge: Masa birleştirme talebi
 * - handleConfirmMergeTables: Masa birleştirme onayı
 * - handleCloseMergeTablesModal: Birleştirme modalını kapat
 * - handleCloseTransferTableModal: Taşıma modalını kapat
 * 
 * QuickSaleScreen.tsx'den ayrıştırıldı - Ocak 2026
 */

import { useCallback } from 'react';
import type { CartItem } from '../../quickSale/types';
import type { NavigateFunction } from 'react-router-dom';
import salesService from '../../../../services/salesService';

// Türkiye saatini ISO formatında döndüren helper fonksiyon
const getTurkeyTimeISO = (): string => {
  const now = new Date();
  const turkeyDateStr = now.toLocaleString('sv-SE', { 
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).replace(' ', 'T');
  
  const ms = now.getMilliseconds().toString().padStart(3, '0');
  return `${turkeyDateStr}.${ms}`;
};

// Hook parametreleri için tip tanımı
export interface UseRestaurantTableOperationsParams {
  // Temel bilgiler
  isRestaurantMode: boolean;
  tableId: string | null;
  tableName: string | null;
  restaurantScreenId: number | null;
  restaurantBranchId: string | null;
  companyId: string | null;
  waiterName: string | null;
  
  // Pending ürün (adisyon açma için)
  pendingProduct: any;
  pendingQuantity: number;
  pendingMultiplier: number;
  
  // Masa birleştirme state'leri
  mergeTargetTableId: number | null;
  mergeTargetTableName: string;
  mergeSourceItems: any[];
  mergeTargetItems: any[];
  isMergingTables: boolean;
  isTransferringTable: boolean;
  
  // Sepet
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  
  // Yetkiler
  waiterPermissions: {
    canTransferTable: boolean;
    canCloseTable: boolean;
    canAddProduct: boolean;
  };
  
  // Navigasyon
  navigate: NavigateFunction;
  
  // Snackbar ve modal
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  showAlertModal: (title: string, message: string) => void;
  
  // State setters
  setIsOpeningTable: React.Dispatch<React.SetStateAction<boolean>>;
  setConfirmOpenTableModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingProduct: React.Dispatch<React.SetStateAction<any>>;
  setPendingQuantity: React.Dispatch<React.SetStateAction<number>>;
  setPendingMultiplier: React.Dispatch<React.SetStateAction<number>>;
  setReservedLongCode: React.Dispatch<React.SetStateAction<string | null>>;
  setReservedShortCode: React.Dispatch<React.SetStateAction<string | null>>;
  setTransferTableModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTransferringTable: React.Dispatch<React.SetStateAction<boolean>>;
  setMergeTargetTableId: React.Dispatch<React.SetStateAction<number | null>>;
  setMergeTargetTableName: React.Dispatch<React.SetStateAction<string>>;
  setMergeSourceItems: React.Dispatch<React.SetStateAction<any[]>>;
  setMergeTargetItems: React.Dispatch<React.SetStateAction<any[]>>;
  setMergeTablesModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMergingTables: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Diğer fonksiyonlar
  addWaiterAction: (activeSaleId: string | number, actionType: string, details: any) => Promise<void>;

  /**
   * ✅ YENİ: Şube fiyatı garanti çözücü (race-condition fix).
   * Adisyon açarken ilk ürün eklenirken kesin fiyatı döndürür; şube fiyatı
   * cache'de yoksa anında çeker. Verilmezse pendingProduct.price'a fallback.
   */
  resolveBranchPrice?: (productId: string) => Promise<{ price: number; vatStatus: 'included' | 'excluded'; usedBranchPrice: boolean } | null>;
}

// Hook dönüş tipi
export interface UseRestaurantTableOperationsReturn {
  handleConfirmOpenTable: () => Promise<void>;
  handleCancelOpenTable: () => void;
  handleOpenTransferTableModal: () => void;
  handleTransferTable: (targetTableId: number, targetTableName: string) => Promise<void>;
  handleRequestMerge: (targetTableId: number, targetTableName: string) => Promise<void>;
  handleConfirmMergeTables: () => Promise<void>;
  handleCloseMergeTablesModal: () => void;
  handleCloseTransferTableModal: () => void;
}

/**
 * Restaurant modu için masa işlemleri hook'u
 */
export const useRestaurantTableOperations = ({
  isRestaurantMode,
  tableId,
  tableName,
  restaurantScreenId,
  restaurantBranchId,
  companyId,
  waiterName,
  pendingProduct,
  pendingQuantity,
  pendingMultiplier,
  mergeTargetTableId,
  mergeTargetTableName,
  mergeSourceItems,
  isMergingTables,
  isTransferringTable,
  setCart,
  waiterPermissions,
  navigate,
  showSnackbar,
  showAlertModal,
  setIsOpeningTable,
  setConfirmOpenTableModalOpen,
  setPendingProduct,
  setPendingQuantity,
  setPendingMultiplier,
  setReservedLongCode,
  setReservedShortCode,
  setTransferTableModalOpen,
  setIsTransferringTable,
  setMergeTargetTableId,
  setMergeTargetTableName,
  setMergeSourceItems,
  setMergeTargetItems,
  setMergeTablesModalOpen,
  setIsMergingTables,
  addWaiterAction,
  resolveBranchPrice,
}: UseRestaurantTableOperationsParams): UseRestaurantTableOperationsReturn => {

  /**
   * Adisyon Açma Onayı - Kullanıcı onayladı, masa aç ve ürün ekle
   */
  const handleConfirmOpenTable = useCallback(async () => {
    if (!pendingProduct || !isRestaurantMode || !tableId || !restaurantScreenId || !companyId) {
      console.error('❌ [ADISYON AÇMA] Gerekli bilgiler eksik!');
      return;
    }
    
    try {
      setIsOpeningTable(true);
      
      console.log('✅ [ADISYON AÇMA] Kullanıcı onayladı! Masa açılıyor...', {
        tableName,
        product: pendingProduct.name,
        quantity: pendingQuantity
      });

      const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../../services/restaurantApiService');
      
      // Sipariş numarası al
      console.log('🎫 [ADISYON AÇMA] Sipariş numarası rezerve ediliyor...');
      const orderNumberResponse = await salesService.reserveOrderNumber('RESTAURANT', restaurantBranchId || undefined);
      console.log(`🎫 [ADISYON AÇMA] Numara alındı: ${orderNumberResponse.shortCode} (${orderNumberResponse.longCode})`);
      
      // Yeni masa aç - Türkiye saati ile
      const turkeyTime = getTurkeyTimeISO();
      const activeSale = await restaurantActiveSaleApi.create({
        RestaurantScreenId: restaurantScreenId,
        BranchId: restaurantBranchId,
        TableId: parseInt(tableId),
        OpenedBy: waiterName || 'Bilinmeyen',
        Notes: `${tableName} - ${waiterName} tarafından açıldı`,
        OpenedAt: turkeyTime,
        ReservedLongCode: orderNumberResponse.longCode,
        ReservedShortCode: orderNumberResponse.shortCode
      });
      
      console.log('✅ [ADISYON AÇMA] Masa açıldı! ID:', activeSale.Id);
      
      // Numara state'e kaydet
      setReservedLongCode(orderNumberResponse.longCode);
      setReservedShortCode(orderNumberResponse.shortCode);
      
      // "open_table" action'ı kaydet
      await addWaiterAction(activeSale.Id, 'open_table', {
        tableName: tableName,
        tableId: tableId,
        openedAt: getTurkeyTimeISO()
      });

      // ✅ ŞUBE FİYATI GARANTİSİ (race-condition fix):
      // Menü/opsiyonlu ürünlerde fiyat opsiyon farklarıyla geldiğinden
      // dokunmuyoruz. Düz ürünlerde resolveBranchPrice ile kesin fiyatı
      // (gerekirse anında çekerek) alıyoruz; şube fiyatı tanımlıysa kesinlikle
      // kullanılır.
      const hasMenuOrOptions = !!(pendingProduct.menuNormalItems || pendingProduct.selectedOptions || pendingProduct.totalPriceDifference);
      let productPrice = pendingProduct.price || pendingProduct.salePrice || 0;
      let productVatStatus = pendingProduct.vatStatus || 'excluded';
      if (!hasMenuOrOptions && resolveBranchPrice) {
        const resolved = await resolveBranchPrice(pendingProduct.id);
        if (resolved) {
          productPrice = resolved.price;
          productVatStatus = resolved.vatStatus;
          console.log('💰 [ADİSYON AÇMA - ŞUBE FİYAT] Çözülen fiyat:', { product: pendingProduct.name, price: productPrice, usedBranchPrice: resolved.usedBranchPrice });
        }
      }
      const productUnit = pendingProduct.unit || 'adet';
      const productVatRate = pendingProduct.vatRate || 0;
      
      const actualMultiplier = pendingMultiplier > 1 ? pendingMultiplier : 1;
      const newCartItems: any[] = [];

      for (let i = 0; i < actualMultiplier; i++) {
        const createdItem = await restaurantActiveSaleItemApi.create({
          ActiveSaleId: activeSale.Id,
          ProductId: pendingProduct.id,
          ProductName: pendingProduct.name,
          Quantity: pendingQuantity,
          UnitPrice: productPrice,
          DiscountPercent: 0,
          AddedBy: waiterName || 'Bilinmeyen',
          Notes: null
        });

        console.log(`✅ [ADISYON AÇMA] Ürün eklendi (${i + 1}/${actualMultiplier})! SQL ID:`, createdItem.Id);

        await addWaiterAction(activeSale.Id, 'add_item', {
          productId: pendingProduct.id,
          productName: pendingProduct.name,
          quantity: pendingQuantity,
          unitPrice: productPrice,
          sqlItemId: createdItem.Id,
          ...(actualMultiplier > 1 ? { multiplier: actualMultiplier, multiplierIndex: i + 1 } : {})
        });

        const uniqueCartItemId = `${pendingProduct.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-m${i}`;

        newCartItems.push({
          cartItemId: uniqueCartItemId,
          id: pendingProduct.id,
          name: pendingProduct.name,
          price: productPrice,
          quantity: pendingQuantity,
          vatRate: productVatRate,
          vatStatus: productVatStatus as 'included' | 'excluded',
          unit: productUnit,
          sqlItemId: createdItem.Id,
          categoryId: (pendingProduct as any).categoryId || '',
          // ⚠️ KRİTİK: Menü/reçete alanları korunmalı! Masa, menülü bir ürüne
          // tıklanarak açıldığında pendingProduct opsiyon seçimleriyle gelir;
          // bu alanlar düşürülürse ürün sepete "düz ürün" olarak girer ve
          // ödemede HasMenu=false gittiği için menü içeriği stoktan düşmez.
          // (handleNumpadConfirm ile birebir aynı davranış.)
          hasMenu: pendingProduct.hasMenu || false,
          hasRecipe: pendingProduct.hasRecipe || false,
          selectedOptions: pendingProduct.selectedOptions || undefined,
          totalPriceDifference: pendingProduct.totalPriceDifference || undefined,
          menuNormalItems: pendingProduct.menuNormalItems || undefined,
        });
      }

      setCart(prevCart => [...prevCart, ...newCartItems]);
      
      console.log(`✅ [ADISYON AÇMA] İşlem tamamlandı! ${actualMultiplier} adet ürün eklendi.`);
      showSnackbar(`Adisyon açıldı: ${tableName}`, 'success');
      
      setConfirmOpenTableModalOpen(false);
      setPendingProduct(null);
      setPendingQuantity(1);
      setPendingMultiplier(1);
      
    } catch (error: any) {
      console.error('❌ [ADISYON AÇMA] Hata:', error);
      
      if (error.response?.data?.Message?.includes('zaten açık')) {
        showSnackbar('Bu masa zaten açık! Lütfen sayfayı yenileyin.', 'warning');
      } else {
        showSnackbar('Adisyon açılırken hata oluştu!', 'error');
      }
    } finally {
      setIsOpeningTable(false);
    }
  }, [pendingProduct, pendingQuantity, pendingMultiplier, isRestaurantMode, tableId, restaurantScreenId, restaurantBranchId, companyId, waiterName, tableName, showSnackbar, addWaiterAction, setCart, setIsOpeningTable, setConfirmOpenTableModalOpen, setPendingProduct, setPendingQuantity, setPendingMultiplier, setReservedLongCode, setReservedShortCode, resolveBranchPrice]);

  /**
   * Adisyon Açma İptal - Kullanıcı iptal etti
   */
  const handleCancelOpenTable = useCallback(() => {
    console.log('❌ [ADISYON AÇMA] Kullanıcı iptal etti');
    setConfirmOpenTableModalOpen(false);
    setPendingProduct(null);
    setPendingQuantity(1);
    setPendingMultiplier(1);
  }, [setConfirmOpenTableModalOpen, setPendingProduct, setPendingQuantity, setPendingMultiplier]);

  /**
   * Masa Taşıma Modalını Aç
   */
  const handleOpenTransferTableModal = useCallback(() => {
    if (!waiterPermissions.canTransferTable) {
      showAlertModal("Yetki Hatası", "Bu garsona masa taşıma yetkisi verilmemiş!");
      return;
    }
    console.log('🔄 [MASA TAŞI] Modal açılıyor...');
    setTransferTableModalOpen(true);
  }, [waiterPermissions.canTransferTable, showAlertModal, setTransferTableModalOpen]);

  /**
   * Masa Taşıma İşlemini Gerçekleştir
   */
  const handleTransferTable = useCallback(async (targetTableId: number, targetTableName: string) => {
    if (!isRestaurantMode || !tableId || !restaurantScreenId) {
      showAlertModal("Hata", "Masa taşıma için gerekli bilgiler eksik!");
      return;
    }

    try {
      setIsTransferringTable(true);
      console.log('🔄 [MASA TAŞI] İşlem başlatılıyor...', {
        fromTableId: tableId,
        fromTableName: tableName,
        toTableId: targetTableId,
        toTableName: targetTableName,
      });

      const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');

      // Kaynak masanın aktif satışını al
      const sourceActiveSale = await restaurantActiveSaleApi.getByTableId(Number(tableId));
      if (!sourceActiveSale) {
        showAlertModal("Hata", `${tableName} aktif değil!`);
        setIsTransferringTable(false);
        return;
      }

      // SADECE TableId'yi güncelle
      await restaurantActiveSaleApi.update(sourceActiveSale.Id, {
        TableId: targetTableId,
      });

      // Waiter action log
      await addWaiterAction(sourceActiveSale.Id, 'transfer_table', {
        fromTableId: tableId,
        fromTableName: tableName,
        toTableId: targetTableId,
        toTableName: targetTableName,
      });

      console.log('✅ [MASA TAŞI] Başarılı!');
      showSnackbar(`${tableName} → ${targetTableName} taşındı!`, 'success');
      
      // RestaurantScreen'e geri dön
      setTimeout(() => {
        navigate(`/restaurant-screen/${restaurantScreenId}`);
      }, 500);

    } catch (error: any) {
      console.error('❌ [MASA TAŞI] Hata:', error);
      showAlertModal(
        "Masa Taşıma Hatası",
        error.response?.data?.Message || error.message || "Masa taşınırken bir hata oluştu!"
      );
    } finally {
      setIsTransferringTable(false);
      setTransferTableModalOpen(false);
    }
  }, [isRestaurantMode, tableId, tableName, restaurantScreenId, showAlertModal, showSnackbar, addWaiterAction, navigate, setIsTransferringTable, setTransferTableModalOpen]);

  /**
   * Masa Birleştirme Talebi - Sepetleri yükle ve modal aç
   */
  const handleRequestMerge = useCallback(async (targetTableId: number, targetTableName: string) => {
    if (!isRestaurantMode || !tableId || !restaurantScreenId) {
      showAlertModal("Hata", "Masa birleştirme için gerekli bilgiler eksik!");
      return;
    }

    try {
      console.log('🔗 [MASA BİRLEŞTİR] Sepetler yükleniyor...', {
        sourceTableId: tableId,
        sourceTableName: tableName,
        targetTableId,
        targetTableName,
      });

      const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../../services/restaurantApiService');

      // Kaynak masanın aktif satışını ve itemlarını al
      const sourceActiveSale = await restaurantActiveSaleApi.getByTableId(Number(tableId));
      if (!sourceActiveSale) {
        showAlertModal("Hata", `${tableName} aktif değil!`);
        return;
      }

      // Hedef masanın aktif satışını ve itemlarını al
      const targetActiveSale = await restaurantActiveSaleApi.getByTableId(targetTableId);
      if (!targetActiveSale) {
        showAlertModal("Hata", `${targetTableName} aktif değil!`);
        return;
      }

      // Her iki masanın itemlarını çek
      const [sourceItems, targetItems] = await Promise.all([
        restaurantActiveSaleItemApi.getByActiveSaleId(sourceActiveSale.Id),
        restaurantActiveSaleItemApi.getByActiveSaleId(targetActiveSale.Id),
      ]);

      console.log('✅ [MASA BİRLEŞTİR] Sepetler yüklendi:', {
        sourceItems: sourceItems.length,
        targetItems: targetItems.length,
      });

      // State'leri set et ve modal aç
      setMergeTargetTableId(targetTableId);
      setMergeTargetTableName(targetTableName);
      setMergeSourceItems(sourceItems);
      setMergeTargetItems(targetItems);
      setMergeTablesModalOpen(true);
      setTransferTableModalOpen(false);

    } catch (error: any) {
      console.error('❌ [MASA BİRLEŞTİR] Sepetler yüklenirken hata:', error);
      showAlertModal(
        "Yükleme Hatası",
        error.response?.data?.Message || error.message || "Sepetler yüklenirken bir hata oluştu!"
      );
    }
  }, [isRestaurantMode, tableId, tableName, restaurantScreenId, showAlertModal, setMergeTargetTableId, setMergeTargetTableName, setMergeSourceItems, setMergeTargetItems, setMergeTablesModalOpen, setTransferTableModalOpen]);

  /**
   * Masa Birleştirme Onayı - İtemleri hedef masaya aktar
   */
  const handleConfirmMergeTables = useCallback(async () => {
    if (!isRestaurantMode || !tableId || !mergeTargetTableId || !restaurantScreenId) {
      showAlertModal("Hata", "Masa birleştirme için gerekli bilgiler eksik!");
      return;
    }

    try {
      setIsMergingTables(true);
      console.log('🔗 [MASA BİRLEŞTİR] İşlem başlatılıyor...', {
        sourceTableId: tableId,
        sourceTableName: tableName,
        targetTableId: mergeTargetTableId,
        targetTableName: mergeTargetTableName,
      });

      const { restaurantActiveSaleApi, restaurantSplitApi } = await import('../../../../services/restaurantApiService');

      // Kaynak ve hedef masanın aktif satışlarını al
      const [sourceActiveSale, targetActiveSale] = await Promise.all([
        restaurantActiveSaleApi.getByTableId(Number(tableId)),
        restaurantActiveSaleApi.getByTableId(mergeTargetTableId),
      ]);

      if (!sourceActiveSale || !targetActiveSale) {
        showAlertModal("Hata", "Masa bilgileri bulunamadı!");
        setIsMergingTables(false);
        return;
      }

      // ✅ Kaynak masanın itemlarını backend move-item ile hedefe taşı.
      // create+delete yerine move-item: PrintedQuantity (mutfağa yazdırıldı
      // bilgisi) korunur, hedefte aynı ürün varsa birleşir, her iki sale'in
      // toplamları sunucu tarafında yeniden hesaplanır.
      for (const item of mergeSourceItems) {
        await restaurantSplitApi.moveItem({
          ItemId: item.Id,
          FromSaleId: sourceActiveSale.Id,
          ToSaleId: targetActiveSale.Id,
          Quantity: item.Quantity,
          MovedBy: waiterName || undefined,
        });
      }

      // Waiter action log - hedef masaya
      await addWaiterAction(targetActiveSale.Id, 'merge_tables', {
        fromTableId: tableId,
        fromTableName: tableName,
        toTableId: mergeTargetTableId,
        toTableName: mergeTargetTableName,
        itemsMerged: mergeSourceItems.length,
      });

      // Kaynak masa artık boş - sale kaydını sil
      await restaurantActiveSaleApi.delete(sourceActiveSale.Id);

      console.log('✅ [MASA BİRLEŞTİR] Başarılı!');
      showSnackbar(`${tableName} + ${mergeTargetTableName} birleştirildi!`, 'success');

      // RestaurantScreen'e geri dön
      setTimeout(() => {
        navigate(`/restaurant-screen/${restaurantScreenId}`);
      }, 500);

    } catch (error: any) {
      console.error('❌ [MASA BİRLEŞTİR] Hata:', error);
      showAlertModal(
        "Masa Birleştirme Hatası",
        error.response?.data?.Message || error.message || "Masalar birleştirilirken bir hata oluştu!"
      );
    } finally {
      setIsMergingTables(false);
      setMergeTablesModalOpen(false);
    }
  }, [
    isRestaurantMode,
    tableId,
    tableName,
    mergeTargetTableId,
    mergeTargetTableName,
    mergeSourceItems,
    restaurantScreenId,
    showAlertModal,
    showSnackbar,
    addWaiterAction,
    navigate,
    waiterName,
    setIsMergingTables,
    setMergeTablesModalOpen,
  ]);

  /**
   * Masa Birleştirme Modalını Kapat
   */
  const handleCloseMergeTablesModal = useCallback(() => {
    if (!isMergingTables) {
      setMergeTablesModalOpen(false);
      setMergeTargetTableId(null);
      setMergeTargetTableName('');
      setMergeSourceItems([]);
      setMergeTargetItems([]);
    }
  }, [isMergingTables, setMergeTablesModalOpen, setMergeTargetTableId, setMergeTargetTableName, setMergeSourceItems, setMergeTargetItems]);

  /**
   * Masa Taşıma Modalını Kapat
   */
  const handleCloseTransferTableModal = useCallback(() => {
    if (!isTransferringTable) {
      setTransferTableModalOpen(false);
    }
  }, [isTransferringTable, setTransferTableModalOpen]);

  return {
    handleConfirmOpenTable,
    handleCancelOpenTable,
    handleOpenTransferTableModal,
    handleTransferTable,
    handleRequestMerge,
    handleConfirmMergeTables,
    handleCloseMergeTablesModal,
    handleCloseTransferTableModal,
  };
};

export default useRestaurantTableOperations;
